// @flow
import is from 'mz-utils/is'
import shallowEqual from 'mz-utils/shallowEqual'
import { useEffect, useReducer, useRef, useMemo, useCallback } from 'react'

// Types
type ActionType<Data> =
  | { type: 'loading' }
  | { type: 'partial', payload: { data: Data } }
  | { type: 'success', payload: { data: Data } }
  | { type: 'error', payload: { error: any } }

type DispatchType<Data> = (ActionType<Data>) => void

type CancelHandlerSetter = (Function) => void

type State<Data> = {
  data: ?Data,
  loading: boolean,
  error: ?any,
}

type Actions<Data> = {
  retry: () => void,
  cancel: () => void,
  dispatch: DispatchType<Data>,
  success: (Data) => void,
  error: (error: any) => void,
  partial: (Data) => void,
  loading: () => void,
  isIterationActive: () => boolean,
}

type ReducerType<D> = (State<D>, ActionType<D>) => State<D>

/**
 * Internal data loading reducer
 * @param  {Object} state
 * @param  {ActionType} action
 * @return {Object}
 */
const reducer = (state, action) => {
  switch (action.type) {
    case 'loading':
      return { ...state, loading: true }
    case 'partial':
    case 'success':
      return {
        ...state,
        data: action.payload.data,
        error: null,
        loading: action.type === 'partial',
      }
    case 'error':
      return {
        ...state,
        error: action.payload.error,
        loading: false,
      }
    default:
      return state
  }
}

/**
 * `useDataLoader` allow only one argument to be passed to the data loader.
 * When we need to pass an object instead of primitive, general `useEffect`
 * will be triggered whenever the object reference changed, so options object
 * will have to be memoized which is a bad dev experience. To improve that,
 * this hook wrap general `useEffect` and compare dependencies with shallow
 * comparator.
 * @param  {Function} callback     [description]
 * @param  {Array}   dependencies
 */
function useShallowCompare(useHook: any, callback: any, dependencies: any) {
  const depsRef = useRef()
  const depsChanged =
    !depsRef.current ||
    depsRef.current.some((x, i) => !shallowEqual(x, dependencies[i]))

  if (depsChanged) {
    depsRef.current = dependencies
  }

  return useHook(callback, depsRef.current)
}
const useShallowMemo: typeof useMemo = (callback, dependencies) => {
  return (useShallowCompare(useMemo, callback, dependencies): any)
}
const useShallowEffect: typeof useEffect = (callback, dependencies) => {
  return (useShallowCompare(useEffect, callback, dependencies): any)
}

/**
 * Data loader hook which automatically re-fetch the data when options
 * object changed. Returns an object with data, error and loading fields.
 * The data loader function receive options object as a first argument
 * and dispatch function as a second argument. You can use dispatch
 * to implement incremental data loading
 * @param {Function} loader
 * @param {any?} options
 */
export function useDataLoader<D>(
  loader: (State<D>, Actions<D>, CancelHandlerSetter) => ?D | Promise<?D>,
  inputs: ?$ReadOnlyArray<mixed>
): [State<D>, Actions<D>] {
  const iterRef = useRef()
  const cancelRef = useRef()
  const normInputs = inputs || []
  const [rerendeCounter, rerender] = useReducer((c) => c + 1, 0)
  const [state, dispatch] = useReducer<State<D>, ActionType<D>>(
    ((reducer: any): ReducerType<D>),
    {
      data: null,
      error: null,
      loading: true,
    }
  )

  // Increase iteration when deps or nonce changed. That counter is used
  // to determine when we need to do another call to loader, so we use it
  // as a dependency for loader effect and other hooks below
  const activeIteration = useShallowMemo(() => {
    iterRef.current = (iterRef.current || 0) + 1
    return iterRef.current
  }, [rerendeCounter, ...normInputs])

  const safeDispatch = useCallback(
    (action: ActionType<D>) => {
      if (activeIteration === iterRef.current) {
        dispatch(action)
      }
    },
    [activeIteration]
  )

  const setCancelHandler = (cancelFunc) => {
    cancelRef.current = cancelFunc
  }

  const actions = {
    dispatch: safeDispatch,
    retry: useCallback(() => rerender(Date.now()), [rerender]),
    cancel: useCallback(() => {
      if (cancelRef.current && typeof cancelRef.current.cancel === 'function') {
        cancelRef.current()
      }
    }, [cancelRef]),
    success: useCallback(
      (data) => safeDispatch({ type: 'success', payload: { data } }),
      [safeDispatch]
    ),
    partial: useCallback(
      (data) => safeDispatch({ type: 'partial', payload: { data } }),
      [safeDispatch]
    ),
    error: useCallback(
      (error) => safeDispatch({ type: 'error', payload: { error } }),
      [safeDispatch]
    ),
    loading: useCallback(() => safeDispatch({ type: 'loading' }), [
      safeDispatch,
    ]),
    isIterationActive: useCallback(() => activeIteration === iterRef.current, [
      activeIteration,
    ]),
  }

  // Main data loading logic effect
  useShallowEffect(() => {
    actions.loading()

    try {
      actions.cancel()
      const result = loader(state, actions, setCancelHandler)

      if (is.promise(result)) {
        const promiseResult = ((result: any): Promise<D>)
        promiseResult
          .then((data) => {
            actions.success(data)
          })
          .catch((error) => {
            actions.error(error)
          })
      } else {
        const syncResult = ((result: any): D)
        actions.success(syncResult)
      }
    } catch (error) {
      actions.error(error)
    }

    return () => {
      actions.cancel()
    }
  }, [activeIteration])

  return [state, actions]
}

export default useDataLoader
